import { forwardRef } from "react";

import { FormField, useFormContext } from "@ui/components/Form";
import { Select } from "@ui/components/Inputs/Select";
import { TextInput } from "@ui/components/Inputs/TextInput";

import { useSavBlocking } from "../../../../SavBlockingContext";
import type { DateInputProps } from "../../BirthDateFormInputs";

import styles from "../../BirthDateFormInputs.module.scss";

export const YEAR_INPUT_MAX_LENGTH = 4 as const;
export const MAXIMUM_YEARS_RANGE = 100 as const;
export const DEFAULT_START_YEAR = 1900 as const;

type YearInputProps = DateInputProps & {
  minimumAge: number;
};

export const YearInput = forwardRef<HTMLInputElement, YearInputProps>(
  ({ placeholder, ariaLabel, onKeyUp, type, minimumAge }, yearInputRef) => {
    const { formState } = useFormContext();
    const { isBlockingEnabled } = useSavBlocking();

    const currentYear = new Date().getFullYear();
    // With Blocking Timeframe enabled, underage years must be selectable so the block
    // can be triggered — the range spans [currentYear - MAXIMUM_YEARS_RANGE, currentYear].
    const { startYear, endYear } = isBlockingEnabled
      ? { startYear: currentYear - MAXIMUM_YEARS_RANGE, endYear: currentYear }
      : { startYear: DEFAULT_START_YEAR, endYear: currentYear - minimumAge };

    const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => ({
      value: String(endYear - i),
      label: String(endYear - i),
    }));

    if (type === "select") {
      return (
        <FormField
          name="year"
          validation={{
            required: true,
          }}
          ref={yearInputRef}
          className={styles.selectInput}
          render={({ field }) => (
            <Select
              {...field}
              options={years}
              placeholder={placeholder}
              aria-label={ariaLabel}
              dataTestId="year-input"
              isError={!!formState.errors.year}
              isSearchable={false}
              menuPosition="fixed"
              required
            />
          )}
        />
      );
    }
    return (
      <FormField
        name="year"
        validation={{
          required: true,
          validate: {
            dateValid: (year) => year >= 1800 && year <= currentYear,
          },
        }}
        render={({ field }) => (
          <TextInput
            placeholder={placeholder}
            aria-label={ariaLabel}
            maxLength={YEAR_INPUT_MAX_LENGTH}
            {...field}
            type="text"
            inputMode="numeric"
            data-testid="year-input"
            ref={yearInputRef}
            onKeyUp={onKeyUp}
            isError={!!formState.errors.year}
          />
        )}
      />
    );
  }
);
